/**
 * Clip range: process only part of a video (AI summary / AI subtitles).
 * Shared by the client (picker, estimates) and the server (validation,
 * charging), so both sides agree on what a valid range is.
 */

export type ClipTimestampMode = "original" | "zero";

export type ClipRange = {
  startSeconds: number;
  endSeconds: number;
  lengthSeconds: number;
};

export type ClipRangeInput = {
  startSeconds?: unknown;
  endSeconds?: unknown;
};

/** UI state of the range picker (components/common/ClipRangeField.vue). */
export type ClipRangeState = {
  enabled: boolean;
  startSeconds: number;
  endSeconds: number;
  timestampMode: ClipTimestampMode;
};

/** What the picker can play to help find the cut points. */
export type ClipRangeSource =
  | { kind: "youtube"; videoId: string }
  | { kind: "file"; file: File }
  | { kind: "none" };

export const createClipRangeState = (): ClipRangeState => ({
  enabled: false,
  startSeconds: 0,
  endSeconds: 0,
  timestampMode: "original",
});

export type ClipRangeResult =
  | { ok: true; clip: ClipRange | null }
  | { ok: false; code: "INVALID_CLIP_RANGE" | "CLIP_NEEDS_DURATION"; message: string };

/** Shortest clip worth processing; also keeps a stray click from billing 1s. */
export const MIN_CLIP_SECONDS = 10;

/** "1:02:03", "62:03", "45" or "1:02:03.5" → seconds; null when unparseable. */
export const parseTimecode = (value: string): number | null => {
  const text = `${value ?? ""}`.trim();
  if (!text) return null;
  const parts = text.split(":");
  if (parts.length > 3) return null;
  let total = 0;
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i].trim();
    const isLast = i === parts.length - 1;
    if (!(isLast ? /^\d+(\.\d+)?$/ : /^\d+$/).test(part)) return null;
    const n = Number(part);
    // Minutes/seconds after the first field must stay below 60.
    if (i > 0 && n >= 60) return null;
    total = total * 60 + n;
  }
  return Number.isFinite(total) ? total : null;
};

/** Seconds → "mm:ss", or "hh:mm:ss" once past 1h (or when forced). */
export const formatTimecode = (seconds: number, forceHours = false): string => {
  const total = Math.max(0, Math.floor(seconds || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return h > 0 || forceHours ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

const toSeconds = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Validates a requested range against the media length.
 * - No range (both bounds empty) → { ok, clip: null }: process the whole media.
 * - A range covering the whole media also collapses to null.
 * - `end` is clamped to the duration. When the duration is unknown the range
 *   is refused (unless `allowUnknownDuration`): credits are reserved from the
 *   range length, and an end typed past the real end would overcharge.
 */
export const normalizeClipRange = (
  input: ClipRangeInput | null | undefined,
  durationSeconds: number | null | undefined,
  options: { allowUnknownDuration?: boolean } = {}
): ClipRangeResult => {
  const rawStart = toSeconds(input?.startSeconds);
  const rawEnd = toSeconds(input?.endSeconds);
  if (rawStart === null && rawEnd === null) return { ok: true, clip: null };

  const duration = Math.max(0, Number(durationSeconds) || 0);
  if (!duration && !options.allowUnknownDuration) {
    return {
      ok: false,
      code: "CLIP_NEEDS_DURATION",
      message: "The video length is unknown, so a time range can't be applied",
    };
  }

  const start = Math.max(0, Math.floor(rawStart ?? 0));
  let end = rawEnd === null ? duration : Math.ceil(rawEnd);
  if (duration) end = Math.min(end, Math.ceil(duration));

  if (!Number.isFinite(end) || end <= start || end - start < MIN_CLIP_SECONDS) {
    return {
      ok: false,
      code: "INVALID_CLIP_RANGE",
      message: `The time range must be at least ${MIN_CLIP_SECONDS} seconds long and end after it starts`,
    };
  }

  if (duration && start === 0 && end >= Math.floor(duration)) {
    return { ok: true, clip: null };
  }

  return { ok: true, clip: { startSeconds: start, endSeconds: end, lengthSeconds: end - start } };
};

export const normalizeClipTimestampMode = (value: unknown): ClipTimestampMode =>
  value === "zero" ? "zero" : "original";

const TIMESTAMP_LINE_RE = /^\[(\d{1,2}:\d{2}(?::\d{2})?)\]\s/;

/**
 * Keeps only the "[mm:ss] text" lines whose cue starts inside the clip.
 * Lines without a timestamp are kept so a plain-text transcript passes through.
 */
export const filterTimestampedTextByClip = (text: string, clip: ClipRange | null): string => {
  if (!clip || !text) return text;
  return text
    .split("\n")
    .filter((line) => {
      const match = line.match(TIMESTAMP_LINE_RE);
      if (!match) return true;
      const at = parseTimecode(match[1]);
      return at === null || (at >= clip.startSeconds && at < clip.endSeconds);
    })
    .join("\n");
};
